import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useServerFn } from '@tanstack/react-start'
import { AuthCard, AuthForm } from '@/components/auth'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { signInFn } from '@/server/functions/auth'

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

type SignInFormData = z.infer<typeof signInSchema>

export const Route = createFileRoute('/_auth/sign-in')({
  component: SignInPage,
})

function SignInPage() {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const signIn = useServerFn(signInFn)

  const form = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: SignInFormData) => {
    setIsLoading(true)
    try {
      await signIn({ data })
      navigate({ to: '/dashboard' })
    } catch (error: unknown) {
      const err = error as { message?: string }
      form.setError('root', {
        message: err?.message || 'Invalid credentials',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthCard
      title="Sign In"
      description="Access your link management dashboard"
    >
      <AuthForm
        schema={signInSchema}
        defaultValues={{ email: '', password: '' }}
        onSubmit={onSubmit}
        submitText="SIGN IN"
        loadingText="AUTHENTICATING..."
        isLoading={isLoading}
        form={form}
      >
        {(form) => (
          <>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#8E9196] font-mono text-sm">
                    EMAIL
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="user@example.com"
                      className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono focus:border-[#9B87F5] placeholder:text-[#8E9196]/50"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400 font-mono text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#8E9196] font-mono text-sm">
                    PASSWORD
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      placeholder="••••••••"
                      className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono focus:border-[#9B87F5] placeholder:text-[#8E9196]/50"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400 font-mono text-xs" />
                </FormItem>
              )}
            />
          </>
        )}
      </AuthForm>

      <div className="mt-6 text-center">
        <p className="text-[#8E9196] font-mono text-sm">
          // Don't have an account?{' '}
          <Link
            to="/sign-up"
            className="text-[#9B87F5] hover:text-white transition-colors"
          >
            SIGN_UP
          </Link>
        </p>
      </div>
    </AuthCard>
  )
}
